/// <amd-module name="Test/Analytics/CardMetricsCommon/Sources/CardMetricsSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { TrendChartCommonSource } from "Test/Analytics/TrendChartCommon/Sources/TrendChartCommonSource";
import { ITrendChartData } from "Test/Analytics/TrendChartCommon/Types";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { ITfsPageService } from "Tfs/Platform/Page";
import { IVssPageContext } from "VSS/Platform/Context";

export class CardMetricsSource extends TrendChartCommonSource {
    constructor(pageContext: IVssPageContext) {
        super("TestResultsAnalytics_InContext_CardMetricsReport", pageContext);
    }

    public queryTestOutcomeCountMetricDataTestRunTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<CommonTypes.ICardMetricsData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");

        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testRunEntityName,
                project: projectId
            } as ODataQueryOptions;

            const workflow: string = testReportContext.contextType === TCMContracts.TestResultsContextType.Build ? "Build" : "Release";
            const filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                confValues.trendBy,
                confValues.period
            )} and ((Workflow eq '${workflow}')))`;

            const aggrStr = `aggregate(${this._getTotalCountStringForAggregation()}, ${this._getOutcomeCountStringForAggregation(
                CommonTypes.TestOutcome.Passed
            )},
${this._getOutcomeCountStringForAggregation(CommonTypes.TestOutcome.Failed)}, ${this._getOutcomeCountStringForAggregation(
                CommonTypes.TestOutcome.NotExecuted
            )}, ${this._getOutcomeCountStringForAggregation(CommonTypes.TestOutcome.NotImpacted)})`;

            queryOptions.$apply = `${filterStr}/${aggrStr}`;

            return this._queryTestOutcomeCountMetricData(queryOptions, confValues);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    public queryDefaultTrendChartData(testReportContext: CommonTypes.ITestReportContext): Promise<ITrendChartData> {
        return super._queryDefaultTestOutcomeCountAndPassRateTrendData_TestRunTable(testReportContext);
    }

    private _queryTestOutcomeCountMetricData(
        queryOptions: ODataQueryOptions,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<CommonTypes.ICardMetricsData> {
        const entityWiseOutcomeParseFunction: Function = this._getEntityWiseOutcomeCountParseFunction(queryOptions.entityType);
        const entityWiseTotalCountParseFunction: Function = this._getEntityWiseTotalCountParseFunction(queryOptions.entityType);

        return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
            if (!data || !data.value) {
                throw new Error("data is empty.");
            }
            const metricsData: CommonTypes.ICardMetricsData = {
                passedCount: 0,
                failedCount: 0,
                notExecutedCount: 0,
                notImpactedCount: 0,
                passPercentage: 0,
                totalCount: 0,
                confValues: confValues,
                isCachedData: data.isCachedData
            } as CommonTypes.ICardMetricsData;

            // Iterate over all data. There should be only item in list as we are fetching aggregates and not group.
            (data.value as any[]).forEach(d => {
                metricsData.totalCount = entityWiseTotalCountParseFunction(d);
                metricsData.notImpactedCount = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.NotImpacted, d);
                metricsData.passedCount = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.Passed, d);
                metricsData.failedCount = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.Failed, d);
                metricsData.notExecutedCount = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.NotExecuted, d);

                metricsData.passPercentage = this._getPassPercentage(
                    metricsData.totalCount,
                    metricsData.passedCount,
                    metricsData.notExecutedCount,
                    metricsData.notImpactedCount
                );
            });
            return metricsData;
        });
    }
}
